import { atom } from "nanostores";

import { useStore } from "@nanostores/react";
import { getAppConfig } from "@vega-core/utils/appConfig";
import { isLocal } from "@vega-core/utils/global";

import { FeatureFlag } from "./featureFlagEnum";
import type { FeatureFlagKeys } from "./useFeatureFlag";

const STORAGE_KEY = "vega:ff-overrides";

type Overrides = Partial<Record<FeatureFlagKeys, boolean>>;

/**
 * Flags the dev overlay must NEVER override — overriding them would brick
 * the app and lock the developer out of the palette itself.
 */
export const NON_OVERRIDABLE_FLAGS: ReadonlySet<FeatureFlagKeys> = new Set([
  FeatureFlag.MAINTENANCE_MODE,
]);

export function isDevOverlayEnvironment(): boolean {
  return isLocal() || getAppConfig().mode === "test";
}

function isOverridable(key: FeatureFlagKeys): boolean {
  return !NON_OVERRIDABLE_FLAGS.has(key);
}

function loadFromStorage(): Overrides {
  if (!isDevOverlayEnvironment()) return {};

  try {
    const raw = localStorage.getItem(STORAGE_KEY);

    return raw === null ? {} : (JSON.parse(raw) as Overrides);
  } catch {
    return {};
  }
}

function saveToStorage(overrides: Overrides): void {
  if (!isDevOverlayEnvironment()) return;

  if (Object.keys(overrides).length === 0) {
    localStorage.removeItem(STORAGE_KEY);

    return;
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(overrides));
}

export const $featureFlagOverrides = atom<Overrides>(loadFromStorage());

export function setOverride(key: FeatureFlagKeys, value: boolean): void {
  if (!isDevOverlayEnvironment()) return;

  if (!isOverridable(key)) return;

  const next = { ...$featureFlagOverrides.get(), [key]: value };
  $featureFlagOverrides.set(next);
  saveToStorage(next);
}

export function clearOverride(key: FeatureFlagKeys): void {
  if (!isDevOverlayEnvironment()) return;

  const current = $featureFlagOverrides.get();
  const next = Object.fromEntries(
    Object.entries(current).filter(([k]) => k !== key),
  ) as Overrides;
  $featureFlagOverrides.set(next);
  saveToStorage(next);
}

export function clearAllOverrides(): void {
  if (!isDevOverlayEnvironment()) return;

  $featureFlagOverrides.set({});
  saveToStorage({});
}

export function setAllOverrides(
  keys: readonly FeatureFlagKeys[],
  value: boolean,
): void {
  if (!isDevOverlayEnvironment()) return;

  const next = Object.fromEntries(
    keys.filter(isOverridable).map((key) => [key, value]),
  ) as Overrides;
  $featureFlagOverrides.set(next);
  saveToStorage(next);
}

export function applyFeatureFlagOverrides<T extends { [key: string]: boolean }>(
  flags: T,
  overrides: Overrides,
): T {
  if (!isDevOverlayEnvironment()) return flags;

  return { ...flags, ...overrides };
}

/**
 * Returns a string key that changes whenever feature flag overrides change.
 * Use as a `key` on a top-level component to force a full remount of the app
 * tree when an override is toggled — so route guards, mount-time redirects,
 * and initial-state hooks pick up the new flag values without a page refresh.
 *
 * Outside dev overlay environments the store is always empty, so the key is
 * a stable constant — no remount cost in uat/prod.
 */
export function useFeatureFlagOverridesKey(): string {
  const overrides = useStore($featureFlagOverrides);

  return JSON.stringify(overrides);
}
